import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { validateTextWithConstraint } from '@shared/textConstraintHelpers'
import { TEXT_CONSTRAINTS } from '@shared/inputConstraints'

type MerchantChainReviewDialogProps = {
  isOpen: boolean
  action: 'approve' | 'reject' | null
  merchantName: string
  chainLabel: string
  onClose: () => void
  onConfirm: (reason: string) => Promise<void>
  isLoading: boolean
}

export function MerchantChainReviewDialog({
  isOpen,
  action,
  merchantName,
  chainLabel,
  onClose,
  onConfirm,
  isLoading,
}: MerchantChainReviewDialogProps) {
  const { t } = useTranslation()
  const cancelRef = useRef<HTMLButtonElement>(null)
  const bgColor = useColorModeValue('white', 'gray.800')
  const [reason, setReason] = useState('')
  const [reasonError, setReasonError] = useState<string | null>(null)
  const reasonConstraint = TEXT_CONSTRAINTS['merchantChain.rejectReason']

  if (!action) return null

  const isReject = action === 'reject'

  const handleConfirm = async () => {
    const validation = validateTextWithConstraint(reason, reasonConstraint, {
      requiredOverride: isReject,
    })

    if (!validation.valid) {
      const message = validation.error
        ? t('merchantChainReview.reasonValidation', { message: validation.error })
        : isReject
          ? t('merchantChainReview.reasonRequired')
          : null
      setReasonError(message)
      return
    }

    await onConfirm(validation.value ?? '')
    setReason('')
    setReasonError(null)
  }

  const handleClose = () => {
    setReason('')
    setReasonError(null)
    onClose()
  }

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={handleClose}
      size="md"
    >
      <AlertDialogOverlay>
        <AlertDialogContent bg={bgColor} mx={4}>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {isReject
              ? t('merchantChainReview.confirmRejectTitle')
              : t('merchantChainReview.confirmApproveTitle')}
          </AlertDialogHeader>

          <AlertDialogBody>
            <VStack align="stretch" spacing={4}>
              <Text>
                {t('merchantChainReview.merchant')}: <Text as="span" fontWeight="semibold">{merchantName}</Text>
              </Text>
              <Text>
                {t('merchantChainReview.chain')}: <Text as="span" fontWeight="semibold">{chainLabel}</Text>
              </Text>
              <Text color={isReject ? 'orange.600' : 'gray.600'} fontSize="sm">
                {isReject
                  ? t('merchantChainReview.rejectDescription')
                  : t('merchantChainReview.approveDescription')}
              </Text>

              <FormControl isRequired={isReject} isInvalid={!!reasonError}>
                <FormLabel fontSize="sm">{t('merchantChainReview.reasonLabel')}</FormLabel>
                <Textarea
                  value={reason}
                  onChange={(event) => {
                    const nextValue = reasonConstraint.maxLength
                      ? event.target.value.slice(0, reasonConstraint.maxLength)
                      : event.target.value
                    setReason(nextValue)
                    if (reasonError) setReasonError(null)
                  }}
                  placeholder={
                    isReject
                      ? t('merchantChainReview.rejectReasonPlaceholder')
                      : t('merchantChainReview.approveReasonPlaceholder')
                  }
                  rows={3}
                  resize="none"
                  maxLength={reasonConstraint.maxLength ?? undefined}
                />
                {isReject && (
                  <FormHelperText color="red.500">
                    {t('merchantChainReview.reasonRequired')}
                  </FormHelperText>
                )}
                {reasonError && <FormErrorMessage>{reasonError}</FormErrorMessage>}
              </FormControl>
            </VStack>
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={handleClose} size="sm" disabled={isLoading}>
              {t('common.cancel')}
            </Button>
            <Button
              colorScheme={isReject ? 'orange' : 'green'}
              onClick={handleConfirm}
              ml={3}
              size="sm"
              isLoading={isLoading}
              loadingText={t('common.processing')}
              isDisabled={isReject && !reason.trim()}
            >
              {isReject ? t('common.reject') : t('common.approve')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}
